import {
  AdminHeader,
  AdminSpinner,
  Box,
  Disclaimer,
  Div,
  EditPenIcon,
  H3,
  HeaderIconWrapper,
  Pill,
  lightTheme,
} from '@vgrubs/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataComponent } from '../../adminComponents/NoDataComponent/NoDataComponent';
import { queryKeys } from '../../api/queryKeys';
import { AnnouncementActivityFeed } from './components/AnnouncementActivityFeed';
import { AnnouncementFormModal } from './components/AnnouncementFormModal';
import { AnnouncementStatCards } from './components/AnnouncementStatCards';
import {
  AnnouncementViewsChart,
  ViewRange,
} from './components/AnnouncementViewsChart';
import { AnnouncementViewers } from './components/AnnouncementViewers';
import { formatDay, targetsSummary } from './helpers/announcementViewers';
import { useAnnouncementDetail } from './hooks/useAnnouncementDetail';
import { useAnnouncementViewsPerDay } from './hooks/useAnnouncementViewsPerDay';

const CARD_RANGE = 30;

/**
 * One announcement and who saw it: the numbers, views per day, the latest
 * views and dismissals, and everyone who saw it. Admin and support only, like
 * the list. Editing opens the same wizard the list does.
 */
export const AnnouncementDetails = () => {
  const { id: idParam } = useParams() as { id: string };
  const id = Number(idParam);
  const queryClient = useQueryClient();

  const { announcement, isLoading, notFound } = useAnnouncementDetail(id);
  // The cards always cover the last 30 days and the chart's switch leaves
  // them alone: a toggle inside the chart card changing numbers outside it
  // read as a bug. At 30 both read the same cached query.
  const { days: cardDays } = useAnnouncementViewsPerDay(id, CARD_RANGE);
  const [range, setRange] = useState<ViewRange>(CARD_RANGE);
  const { days } = useAnnouncementViewsPerDay(id, range);
  const [editing, setEditing] = useState(false);

  return (
    <>
      <AdminHeader title="Announcement Details">
        {announcement && (
          <HeaderIconWrapper onClick={() => setEditing(true)}>
            <EditPenIcon />
          </HeaderIconWrapper>
        )}
      </AdminHeader>

      {isLoading && <AdminSpinner />}

      {notFound && (
        <NoDataComponent
          title="Announcement not found"
          description="It was deleted, or the link is out of date."
        />
      )}

      {announcement && (
        <Div $flex $column $gap="24px" $gapSM="8px" $m="32px 0" $mSM="20px 0">
          <Box $flex $between $alignStart $gap="16px">
            <Div $minWidth="0">
              <H3 $m="0">{announcement.title}</H3>
              <Disclaimer $m="4px 0 0" $color={lightTheme.lightGray}>
                Published{' '}
                {formatDay(announcement.publishedAt ?? announcement.createdAt)}
                {announcement.targets.length > 0 &&
                  ` · ${targetsSummary(announcement.targets)}`}
              </Disclaimer>
            </Div>
            <Pill
              $background={
                announcement.active ? lightTheme.green : lightTheme.lightGray
              }
              $minWidth="64px"
              $minWidthSM="64px"
            >
              <Disclaimer $m="0" $color={lightTheme.white}>
                {announcement.active ? 'Active' : 'Inactive'}
              </Disclaimer>
            </Pill>
          </Box>

          <AnnouncementStatCards
            seenCount={announcement.seenCount}
            days={cardDays}
          />

          <Div
            $grid
            $gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            $gridTemplateColumnsSM="minmax(0, 1fr)"
            $gap="24px"
            $gapSM="8px"
          >
            <AnnouncementViewsChart
              days={days}
              range={range}
              onRange={setRange}
              seenCount={announcement.seenCount}
            />
            <AnnouncementActivityFeed id={id} />
          </Div>

          <AnnouncementViewers id={id} seenCount={announcement.seenCount} />
        </Div>
      )}

      <AnnouncementFormModal
        isOpen={editing}
        closeModal={() => setEditing(false)}
        announcement={announcement}
        onSaved={() =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.admin.announcements.all,
          })
        }
      />
    </>
  );
};
